/*
 * Slide-out cart (fromJae, 2026-07-25).
 *
 * Self-contained on purpose: cart.js only loads on the cart page, but the
 * drawer lives on every page, so quantity and remove are handled here rather
 * than by extending the cart page's components.
 *
 * After any change it asks Shopify to re-render the cart-drawer section and
 * swaps the new markup in, which is the same mechanism the cart page uses.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onBodyKeyup = this.onBodyKeyup.bind(this);
    this.bind();
    this.wireCartIcon();
  }

  bind() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]') || event.target.closest('#CartDrawer-Overlay')) {
        event.preventDefault();
        this.close();
        return;
      }
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.removeLine(remove);
        return;
      }
      if (event.target.closest('[data-cart-note-toggle]')) {
        event.preventDefault();
        this.toggleNote();
      }
    });

    // The note is saved as the shopper types (a short pause), and once more
    // if the panel closes or checkout starts before that pause is over.
    this.addEventListener('input', (event) => {
      if (!event.target.matches('[data-cart-note-input]')) return;
      this.noteDraft = event.target.value;
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => this.saveNote(), 500);
    });
    this.addEventListener('submit', () => this.saveNote());

    // quantity-input (global.js) fires a bubbling change on its input.
    this.addEventListener('change', (event) => {
      const input = event.target.closest('.quantity__input');
      if (!input) return;
      this.updateQuantity(input.dataset.index, input.value);
    });
  }

  /* The header cart link opens the drawer instead of loading the cart page.
     If anything here fails the link still works as a normal link. */
  wireCartIcon() {
    const links = document.querySelectorAll('#cart-icon-bubble, .header__icon--cart, a[href$="/cart"].fjh-ico');
    links.forEach((link) => {
      if (link.dataset.cartDrawerBound) return;
      link.dataset.cartDrawerBound = 'true';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(link);
      });
    });
  }

  reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  open(triggeredBy) {
    const wasOpen = this.classList.contains('active');
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.lockScroll();
    this.classList.add('active');
    document.body.addEventListener('keyup', this.onBodyKeyup);

    // The contents drift in behind the panel, but only on a real open: a
    // quantity change re-renders the same markup and must not replay it.
    if (!wasOpen && !this.reducedMotion()) {
      this.classList.add('is-opening');
      clearTimeout(this.openingTimer);
      // Longest row delay (380ms) plus its 440ms animation = 820ms, so 1000 clears it.
      this.openingTimer = setTimeout(() => this.classList.remove('is-opening'), 1000);
    }

    const inner = this.querySelector('.cart-drawer__inner');
    if (inner && typeof trapFocus === 'function') {
      trapFocus(inner, this.querySelector('[data-cart-drawer-close]') || inner);
    }
  }

  /* "Add a note" (Figma 3157:15043, Jenny 2026-10-08): the band opens a
     text box above the total. The open state and any unsaved words survive
     the re-render a quantity change triggers (renderContents). */
  toggleNote(open) {
    const note = this.querySelector('[data-cart-note]');
    if (!note) return;
    const next = typeof open === 'boolean' ? open : !note.classList.contains('is-open');
    note.classList.toggle('is-open', next);
    const toggle = note.querySelector('[data-cart-note-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', String(next));
    this.noteOpen = next;
    if (next) {
      const input = note.querySelector('[data-cart-note-input]');
      // After the box has started to open, so the page does not jump to it.
      if (input) setTimeout(() => input.focus({ preventScroll: true }), 200);
    }
  }

  saveNote() {
    clearTimeout(this.noteTimer);
    if (typeof this.noteDraft !== 'string' || this.noteDraft === this.noteSaved) return;
    const note = this.noteDraft;
    this.noteSaved = note;
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body: JSON.stringify({ note }) }).catch(() => {
      // Not saved; the checkout form still carries the note (form attribute).
      this.noteSaved = undefined;
    });
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.saveNote();
    this.classList.remove('active');
    this.classList.remove('is-opening');
    document.body.removeEventListener('keyup', this.onBodyKeyup);
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);

    // Give the page its scrollbar back only once the panel has left, or the
    // width change shunts everything sideways mid-slide.
    clearTimeout(this.unlockTimer);
    this.unlockTimer = setTimeout(() => {
      if (!this.classList.contains('active')) this.unlockScroll();
      // Must outlast the 460ms close transition in component-cart-drawer.css,
      // or the scrollbar returns while the panel is still sliding.
    }, this.reducedMotion() ? 0 : 480);
  }

  /* Freezing the page removes its scrollbar, which on Windows would jolt the
     layout 15px wider exactly as the panel starts moving. */
  lockScroll() {
    clearTimeout(this.unlockTimer);
    if (document.body.classList.contains('cart-drawer-open')) return;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    document.body.classList.add('cart-drawer-open');
  }

  unlockScroll() {
    document.body.classList.remove('cart-drawer-open');
    document.body.style.paddingRight = '';
  }

  onBodyKeyup(event) {
    if (event.code === 'Escape') this.close();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* What the add-to-cart form and this component ask Shopify to re-render. */
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  /* Line totals and the checkout total before a change, so the ones that
     actually moved can be nudged afterwards. */
  snapshotPrices() {
    const before = { lines: {}, total: '' };
    this.querySelectorAll('.cart-item[data-index]').forEach((row) => {
      const price = row.querySelector('.cart-item__totals .price');
      if (price) before.lines[row.dataset.index] = price.textContent.trim();
    });
    const total = this.querySelector('.cart-drawer__subtotal-amount');
    if (total) before.total = total.textContent.trim();
    return before;
  }

  bumpChanged(before) {
    if (!before || this.reducedMotion()) return;
    this.querySelectorAll('.cart-item[data-index]').forEach((row) => {
      const old = before.lines[row.dataset.index];
      row.querySelectorAll('.cart-item__totals .price').forEach((price) => {
        if (old && old !== price.textContent.trim()) this.replay(price, 'fj-flash', 420);
      });
    });
    const total = this.querySelector('.cart-drawer__subtotal-amount');
    // Prices never change size (Jenny 2026-10-08): the line totals and the
    // subtotal dim briefly instead.
    if (total && before.total && before.total !== total.textContent.trim()) {
      this.replay(total, 'fj-flash', 420);
    }
  }

  /* Removing then re-adding in the same frame is what makes an animation run
     again on an element that already played it. */
  replay(element, className, duration) {
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
    setTimeout(() => element.classList.remove(className), duration);
  }

  bumpCartCount() {
    if (this.reducedMotion()) return;
    const count = document.querySelector('.fjh-count, .cart-count-bubble span[aria-hidden="true"]');
    if (count) this.replay(count, 'fj-bump', 500);
  }

  renderContents(parsedState, before) {
    const sections = parsedState && parsedState.sections;
    if (!sections) return;

    const drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      const inner = this.getSectionInnerHTML(drawerHTML, '#CartDrawer');
      if (inner !== null) {
        this.querySelector('#CartDrawer').innerHTML = inner;
        this.classList.toggle('is-empty', /cart-drawer__empty/.test(inner));
        this.restoreNote();
      }
    }

    const bubbleHTML = sections['cart-icon-bubble'];
    const bubbleHost = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && bubbleHost) {
      const inner = this.getSectionInnerHTML(bubbleHTML, '.shopify-section');
      if (inner !== null) bubbleHost.innerHTML = inner;
      this.wireCartIcon();
    }

    this.bumpChanged(before);
    this.bumpCartCount();

    if (this.classList.contains('active')) {
      this.open();
      return;
    }

    // The panel markup was just replaced, so let the browser paint it in its
    // closed position for one frame. Without this the slide is skipped and the
    // panel snaps into place.
    void this.offsetWidth;
    requestAnimationFrame(() => this.open());
  }

  /* Put the note back the way the shopper left it after a re-render: open
     (with no animation) and holding whatever they typed. */
  restoreNote() {
    const note = this.querySelector('[data-cart-note]');
    if (!note) return;
    const input = note.querySelector('[data-cart-note-input]');
    if (input && typeof this.noteDraft === 'string') input.value = this.noteDraft;
    if (this.noteOpen) {
      note.classList.add('is-open', 'no-motion');
      const toggle = note.querySelector('[data-cart-note-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => requestAnimationFrame(() => note.classList.remove('no-motion')));
    }
  }

  setBusy(busy) {
    this.classList.toggle('is-busy', busy);
    this.querySelectorAll('.quantity__button, [data-cart-remove], .quantity__input').forEach((el) => {
      el.disabled = busy;
    });
  }

  /* The row slides out first, then the request goes; the re-render lands after
     it has visually gone, so the list never jumps under a finger. */
  removeLine(button) {
    const row = button.closest('.cart-item');
    const index = button.dataset.index;
    if (!index || (row && row.classList.contains('is-removing'))) return;

    if (!row || this.reducedMotion()) {
      this.updateQuantity(index, 0);
      return;
    }
    row.classList.add('is-removing');
    setTimeout(() => this.updateQuantity(index, 0), 190);
  }

  updateQuantity(line, quantity) {
    if (!line) return;
    const before = this.snapshotPrices();
    this.setBusy(true);
    const body = JSON.stringify({
      line,
      quantity: parseInt(quantity, 10),
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((state) => {
        this.renderContents(state, before);
      })
      .catch(() => {
        // Leave the drawer as-is; the cart page stays available as a fallback.
      })
      .finally(() => this.setBusy(false));
  }
}

customElements.define('cart-drawer', CartDrawer);
